import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Users,
  Activity,
  Clock,
  Target,
  TrendingUp,
  Gamepad2,
  UserCheck,
  ClipboardList,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { useAccessibility } from '../contexts/AccessibilityContext';

/**
 * Interfaces para las estadísticas
 */
interface UserStats {
  total_usuarios: number;
  usuarios_admin: number;
  usuarios_profesional: number;
  usuarios_paciente: number;
  usuarios_individual: number;
  nuevos_usuarios_mes: number;
  usuarios_activos_mes: number;
}

interface ActivityStats {
  total_partidas: number;
  partidas_mes: number;
  tiempo_total: number;
}

interface PopularGame {
  juego_id: number;
  nombre_juego: string;
  codigo_juego: string;
  total_partidas: number;
  usuarios_unicos: number;
  partidas_completadas: number;
  tiempo_promedio: number;
}

interface ProfessionalStats {
  total_profesionales: number;
  profesionales_con_pacientes: number;
  promedio_pacientes_por_profesional: number;
}

interface SessionStats {
  total_sesiones: number;
  sesiones_pendiente: number;
  sesiones_en_progreso: number;
  sesiones_finalizada: number;
  promedio_completitud: number;
}

interface GrowthData {
  mes: string;
  nuevos_usuarios: number;
}

interface ActivityByDay {
  fecha: string;
  partidas: number;
}

interface AdminDashboardStats {
  users: UserStats;
  activity: ActivityStats;
  games: PopularGame[];
  professionals: ProfessionalStats;
  sessions: SessionStats;
  growth: GrowthData[];
  activityByDay: ActivityByDay[];
}

/**
 * Componente de tarjeta de estadística
 */
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
  subtitle?: string;
}

const StatCard = ({ icon, label, value, color, subtitle }: StatCardProps) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`bg-gradient-to-br ${color} p-6 rounded-2xl shadow-lg`}
  >
    <div className="flex items-start justify-between mb-2">
      <div className="text-white/80">{icon}</div>
    </div>
    <div>
      <p className="text-white/80 text-sm mb-1">{label}</p>
      <p className="text-white text-3xl font-bold">{value}</p>
      {subtitle && <p className="text-white/70 text-xs mt-1">{subtitle}</p>}
    </div>
  </motion.div>
);

/**
 * Formatear tiempo en formato hh:mm:ss
 */
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${seconds}s`;
};

/**
 * Formatear fecha corta para gráficas
 */
const formatDateShort = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
  });
};

/**
 * Colores para gráficas de pastel
 */
const PIE_COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#3b82f6'];

/**
 * Página principal de estadísticas de administrador
 */
export default function AdminStatsPage() {
  const { highContrast } = useAccessibility();
  const chartPrimary = highContrast ? '#FFE600' : '#8b5cf6';
  const chartSecondary = highContrast ? '#00ff88' : '#10b981';
  const pieCols = highContrast
    ? ['#FFE600', '#00ff88', '#ff6600', '#ff00cc', '#00ccff']
    : PIE_COLORS;

  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/stats/dashboard');

      if (response.data.ok) {
        setStats(response.data.data);
      } else {
        toast.error('Error al cargar estadísticas');
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <p className="text-white text-xl">No se pudieron cargar las estadísticas</p>
      </div>
    );
  }

  const { users, activity, games, professionals, sessions, growth, activityByDay } = stats;

  // Datos para gráfica de usuarios por rol
  const usersByRole = [
    { name: 'Administradores', value: users.usuarios_admin },
    { name: 'Profesionales', value: users.usuarios_profesional },
    { name: 'Pacientes', value: users.usuarios_paciente },
    { name: 'Individuales', value: users.usuarios_individual },
  ];

  // Datos para gráfica de sesiones por estado
  const sessionsByStatus = [
    { name: 'Pendiente', value: sessions.sesiones_pendiente },
    { name: 'En Progreso', value: sessions.sesiones_en_progreso },
    { name: 'Finalizada', value: sessions.sesiones_finalizada },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-2">Dashboard Administrador</h1>
          <p className="text-gray-300">Vista general del sistema Neureka</p>
        </motion.div>

        {/* Stats Cards Principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<Users className="w-8 h-8" />}
            label="Total Usuarios"
            value={users.total_usuarios}
            color="from-purple-600 to-purple-800"
            subtitle={`${users.nuevos_usuarios_mes} nuevos este mes`}
          />
          <StatCard
            icon={<UserCheck className="w-8 h-8" />}
            label="Usuarios Activos"
            value={users.usuarios_activos_mes}
            color="from-blue-600 to-blue-800"
            subtitle="Este mes"
          />
          <StatCard
            icon={<Users className="w-8 h-8" />}
            label="Total Profesionales"
            value={users.usuarios_profesional}
            color="from-green-600 to-green-800"
          />
          <StatCard
            icon={<Users className="w-8 h-8" />}
            label="Total Pacientes"
            value={users.usuarios_paciente}
            color="from-teal-600 to-teal-800"
          />
          <StatCard
            icon={<Activity className="w-8 h-8" />}
            label="Total Partidas"
            value={activity.total_partidas}
            color="from-orange-600 to-orange-800"
          />
          <StatCard
            icon={<Gamepad2 className="w-8 h-8" />}
            label="Partidas (mes)"
            value={activity.partidas_mes}
            color="from-pink-600 to-pink-800"
          />
          <StatCard
            icon={<ClipboardList className="w-8 h-8" />}
            label="Total Sesiones"
            value={sessions.total_sesiones}
            color="from-indigo-600 to-indigo-800"
          />
          <StatCard
            icon={<Clock className="w-8 h-8" />}
            label="Tiempo Total Sistema"
            value={formatTime(activity.tiempo_total)}
            color="from-red-600 to-red-800"
          />
        </div>

        {/* Gráfica de Crecimiento */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-6 h-6" />
            Crecimiento de Usuarios (Últimos 6 Meses)
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={growth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
              <XAxis dataKey="mes" stroke="#ffffff80" style={{ fontSize: '12px' }} />
              <YAxis stroke="#ffffff80" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="nuevos_usuarios"
                stroke={chartPrimary}
                strokeWidth={3}
                name="Nuevos Usuarios"
                dot={{ fill: chartPrimary, r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Gráfica de Actividad */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Activity className="w-6 h-6" />
            Actividad (Últimos 30 Días)
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={activityByDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
              <XAxis
                dataKey="fecha"
                tickFormatter={formatDateShort}
                stroke="#ffffff80"
                style={{ fontSize: '12px' }}
              />
              <YAxis stroke="#ffffff80" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                }}
                labelFormatter={(label) => formatDateShort(label)}
              />
              <Legend />
              <Bar dataKey="partidas" fill={chartPrimary} name="Partidas" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Top 10 Juegos Más Populares */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Gamepad2 className="w-6 h-6" />
            Top 10 Juegos Más Populares
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={games} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
              <XAxis type="number" stroke="#ffffff80" style={{ fontSize: '12px' }} />
              <YAxis
                type="category"
                dataKey="nombre_juego"
                stroke="#ffffff80"
                style={{ fontSize: '12px' }}
                width={150}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
              <Legend />
              <Bar dataKey="total_partidas" fill={chartPrimary} name="Total Partidas" />
              <Bar dataKey="usuarios_unicos" fill={chartSecondary} name="Usuarios Únicos" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Grid de Stats de Profesionales y Gráficas de Pastel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Stats de Profesionales */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6"
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              Estadísticas de Profesionales
            </h3>
            <div className="space-y-4">
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-gray-300 text-sm mb-1">Total Profesionales</p>
                <p className="text-white text-2xl font-bold">{professionals.total_profesionales}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-gray-300 text-sm mb-1">Con Pacientes Asignados</p>
                <p className="text-white text-2xl font-bold">
                  {professionals.profesionales_con_pacientes}
                </p>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-gray-300 text-sm mb-1">Promedio Pacientes/Profesional</p>
                <p className="text-white text-2xl font-bold">
                  {professionals.promedio_pacientes_por_profesional}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Usuarios por Rol */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6"
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Usuarios por Rol
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={usersByRole}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {usersByRole.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={pieCols[index % pieCols.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Sesiones por Estado */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6"
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Sesiones por Estado
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={sessionsByStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {sessionsByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={pieCols[index % pieCols.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 bg-white/5 rounded-lg p-3">
              <p className="text-gray-300 text-sm mb-1">Completitud Promedio</p>
              <p className="text-white text-xl font-bold">{sessions.promedio_completitud}%</p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
